import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { getSubscriptions, type PortalSubscription } from '../lib/api.js';
import {
  STORES,
  describeSubscription,
  needsStoreFallback,
  storeRoute,
} from '../lib/subscriptions.js';
import { Note, Problem } from '../components/form.js';

/**
 * What this account pays Ridgewill for, across every app (auth#435).
 *
 * ── THIS SCREEN IS WHY SUBSCRIPTIONS LIVE IN AUTH ───────────────────
 *
 * adr/0016 moved subscription ownership out of the products and into auth, and
 * the recorded justification is one place a person can see everything they pay
 * for. Until 2026-09-08 the standing position was the opposite: tier is app
 * data and belongs in the app. The issue says it plainly, so it is worth
 * repeating where somebody might delete this file: if this section goes, that
 * ADR should be revisited rather than left standing.
 *
 * ── READ-ONLY, AND DELIBERATELY SO ──────────────────────────────────
 *
 * Apple and Google own cancellation and payment methods for anything bought
 * through them. A Cancel button here could not honour itself, so the screen
 * links out instead of implying otherwise. Anything bought through a route
 * where Ridgewill is the merchant is undecided (see keyodo#431 on merchant of
 * record) and out of scope; this is the management surface for subscriptions
 * that already exist.
 */
export function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState<PortalSubscription[]>([]);
  /**
   * "Have the requests come back" tracked separately from the data, the same
   * way Agreements does it. Inferring it from a non-empty array is the bug that
   * made that screen say "Loading…" forever against a perfectly good 200: an
   * empty list is a real answer here, and the commonest one.
   */
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await getSubscriptions();
        // `?? []` because `call<T>` casts the body to the shape the call site
        // claims rather than checking it, so a renamed key is a compile-time
        // lie and a runtime `undefined`. Defaulting makes that render an empty
        // list, which is visibly wrong, instead of a spinner that never stops.
        setSubscriptions(res.subscriptions ?? []);
        setLoaded(true);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load your subscriptions.');
      }
    })();
  }, []);

  if (error && !loaded) return <Problem>{error}</Problem>;
  if (!loaded) return <Note>Loading…</Note>;

  const showFallback = needsStoreFallback(subscriptions);

  return (
    <section style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: '1.15rem', marginBottom: '1rem' }}>Subscriptions</h2>

      {subscriptions.length === 0 ? (
        /*
          Said out loud. The endpoint returns rows rather than every registered
          application, so an empty list is the answer for most accounts, and a
          blank panel would read as a screen that had failed to load.
        */
        <Note>You do not have any Ridgewill subscriptions at the moment.</Note>
      ) : (
        <ul style={list}>
          {subscriptions.map((sub) => (
            <SubscriptionRow key={sub.appSlug} sub={sub} />
          ))}
        </ul>
      )}

      <div style={storeBlock}>
        <h3 style={{ fontSize: '1rem', margin: '0 0 .35rem' }}>Changing or cancelling</h3>
        <p style={{ margin: 0, color: C.textMid, fontSize: '.85rem', lineHeight: 1.45 }}>
          Subscriptions bought in an app are billed by the app store, so payment details and
          cancellations are handled there rather than by Ridgewill.
        </p>

        {/*
          THE GENERIC PAIR IS A FALLBACK NOW, NOT THE ANSWER.

          Each row carries its own destination, so repeating two unrelated store
          links underneath them would add noise and quietly undercut the
          specific ones. It shows when it can actually help: a row whose store
          was never recorded, or NO ROWS AT ALL — because "I am being charged
          and it is not listed here" is a real arrival. A purchase made before
          signing in sits on an anonymous store customer until the app
          reconciles it, so somebody can be paying and have no row yet, and the
          store is the only place this screen can send them.

          See `needsStoreFallback` for why this is keyed on the store being
          UNKNOWN rather than on the row having produced no link: a Stripe row
          and a promotional grant produce no link either, and Apple and Google
          are no use to either of them.
        */}
        {showFallback && (
          <>
            <p
              style={{
                margin: '.6rem 0 0',
                color: C.textMid,
                fontSize: '.85rem',
                lineHeight: 1.45,
              }}
            >
              {subscriptions.length === 0
                ? 'If you are being charged for something that is not listed here, check the store you bought it through:'
                : 'For anything above without a link, check the store you bought it through:'}
            </p>
            <ul
              style={{
                ...list,
                marginTop: '.6rem',
                display: 'flex',
                gap: '1.25rem',
                flexWrap: 'wrap',
              }}
            >
              {STORES.map((store) => (
                <li key={store.href}>
                  <a
                    href={store.href}
                    /* A different origin and somebody else's account page:
                       opening it over the top of the portal loses whatever they
                       came here to read. `noreferrer` alongside `noopener`
                       because the target is a third party and the referrer says
                       which page of ours they were on. */
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: C.brand, fontSize: '.85rem' }}
                  >
                    {store.label}
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

function SubscriptionRow({ sub }: { sub: PortalSubscription }) {
  const copy = describeSubscription(sub);
  const route = storeRoute(sub.store);

  return (
    <li style={row}>
      <div style={{ flex: '1 1 12rem', minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{sub.appName}</div>
        {copy.tierLabel && <Note>{copy.tierLabel}</Note>}
      </div>
      <div style={statusColumn}>
        {/* The status carries the tone, and the sentence under it stays muted.
            Toning both makes a failed payment a wall of red, which is harder to
            read than the one line that matters. */}
        <Note tone={copy.tone}>{copy.headline}</Note>
        {copy.detail && <Note>{copy.detail}</Note>}

        {/*
          WHERE THIS PARTICULAR SUBSCRIPTION IS CHANGED — on the row, because
          the answer differs per row. Somebody with Keyodo on Android and
          another app bought on an iPhone needs two different destinations, and
          a single link at the bottom of the screen could only ever be right for
          one of them.

          The sentence renders whether or not there is a link behind it: "billed
          through Stripe" and "given to you by Ridgewill" are both answers, and
          suppressing them would leave a row looking like the one thing the
          screen refused to explain.
        */}
        {route.href ? (
          <Note>
            <a href={route.href} target="_blank" rel="noopener noreferrer" style={storeLink}>
              {route.sentence}
            </a>
          </Note>
        ) : (
          <Note>{route.sentence}</Note>
        )}
      </div>
    </li>
  );
}

const list: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0 };

const row: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  /** `flex-start`, not `center`: a `PAST_DUE` row carries two sentences and its
   *  neighbour carries none, so centring floats the app name into the middle of
   *  nowhere against the tallest row on the screen. */
  alignItems: 'flex-start',
  gap: '1rem',
  flexWrap: 'wrap',
  padding: '.85rem 0',
  borderTop: `1px solid ${C.inputBorder}`,
};

/**
 * A REAL SECOND COLUMN, and left-aligned inside itself.
 *
 * This was `textAlign: 'right'` with no basis, and the `PAST_DUE` row is what
 * showed the problem: its sentence is four times the length of any other, so it
 * consumed the row, pushed the whole status block onto a line of its own, and
 * left "Payment failed" floating under the app name rather than beside it. The
 * row that most needed to read clearly was the one that read worst.
 *
 * The basis is what fixes it — the column takes a fixed share and the sentence
 * wraps inside it, so every row has the same shape whatever it says. Text
 * ALIGNED LEFT within the column, because right-aligning three wrapped lines
 * gives them a ragged left edge and the reader loses the start of each line.
 * The column itself still sits right, which is what keeps the statuses in line
 * with each other.
 */
const statusColumn: React.CSSProperties = {
  flex: '0 1 18rem',
  minWidth: 0,
  textAlign: 'left',
};

/** Inherits the Note's size and line height; only the colour is the link's. */
const storeLink: React.CSSProperties = { color: C.brand };

const storeBlock: React.CSSProperties = {
  marginTop: '2rem',
  paddingTop: '1.25rem',
  borderTop: `1px solid ${C.inputBorder}`,
};
